import React, {useEffect, useRef, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {loadStripe} from '@stripe/stripe-js';
import {EmbeddedCheckout, EmbeddedCheckoutProvider} from '@stripe/react-stripe-js';
import NavBar from '../components/NavBar';
import Footer from '../components/Footer';
import ErrorToast from '../components/ErrorToast';
import {createStripeCheckout} from '../services/subscription-service';
import {SubscriptionType} from "../models/enums/SubscriptionType";

type LocationState = {
    subscriptionType: SubscriptionType,
    email: string
};

const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

const CheckoutPage: React.FC = () => {
    const stripeRef = useRef<ReturnType<typeof loadStripe> | null>(null);
    if (!stripeRef.current && key) {
        stripeRef.current = loadStripe(key);
    }

    const stripePromise = stripeRef.current;
    const location = useLocation();
    const { subscriptionType, email } = (location.state as LocationState) || {};

    const [clientSecret, setClientSecret] = useState<string>();
    const [errorMessage, setErrorMessage] = useState<string>('');

    useEffect(() => {
        if (!key) {
            setErrorMessage(
                'Checkout is temporarily unavailable. Please try again later or contact support.'
            );
        }
    }, []);

    useEffect(() => {
        if (!key) return;
        if (!email || !subscriptionType) {
            setErrorMessage('Missing user info. Please log in to update your subscription.');
            return;
        }

        const createCheckout = async () => {
            try {
                const response = await createStripeCheckout(email, subscriptionType);
                if (response.status === 200 && response.clientSecret) {
                    setClientSecret(response.clientSecret);
                } else {
                    console.error('Invalid response creating stripe checkout', response);
                    setErrorMessage('Unable to checkout at this time. Please try again later.');
                }
            } catch (err: any) {
                console.error('Error creating stripe checkout', err);
                setErrorMessage('Unable to checkout at this time. Please try again later.');
            }
        };

        createCheckout();
    }, [email, subscriptionType]);

    return (
        <div className="min-h-screen flex flex-col bg-white">
            <NavBar />
            <main className="flex-1">
                <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                    <h1 className="text-2xl font-bold text-crumbblue mb-4">Complete your purchase</h1>

                    {!key && (
                        <div data-testid="no-key-error" className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-amber-900">
                            <p className="font-medium mb-2">Checkout unavailable</p>
                            <p className="text-sm">
                                We’re unable to load payment at the moment. Please try again later.
                            </p>
                        </div>
                    )}

                    {!clientSecret && !errorMessage && (
                        <div className="h-64 rounded-2xl border border-gray-200 shadow-sm bg-gray-50 animate-pulse" />
                    )}

                    {clientSecret && (
                        <div className="min-h-[600px]">
                            <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret }}>
                                <EmbeddedCheckout />
                            </EmbeddedCheckoutProvider>
                        </div>
                    )}
                </section>
            </main>
            <Footer />
            <ErrorToast message={errorMessage} clearError={() => setErrorMessage('')} />
        </div>
    );
};

export default CheckoutPage;
